// k-of-n signature verification over canonical JSON — shared by the manifest,
// snapshot sections and point-lookup proofs, so the rules cannot drift between them.
//
// Two rules that are easy to get wrong and are therefore centralised here:
// * the same signing key never counts twice towards the threshold — otherwise one
//   compromised key would clear a k-of-n threshold on its own;
// * the signed material is the CANONICAL form of the JSON body: keys sorted,
//   whitespace removed.

import { canonical } from './canonical-json';
import { utf8 } from './hash';
import { verifyWithKey } from './verify';

/** Counts how many distinct allowed keys produced a valid signature over [material]. */
export async function countValidSignatures(
  material: Uint8Array,
  signatures: unknown,
  allowedKeys: ReadonlyMap<string, string>,
): Promise<number> {
  if (!Array.isArray(signatures)) {
    return 0;
  }
  let matching = 0;
  const counted = new Set<string>();
  for (const item of signatures) {
    if (typeof item !== 'object' || item === null) {
      continue;
    }
    const keyId = (item as Record<string, unknown>)['key_id'];
    const value = (item as Record<string, unknown>)['value'];
    if (typeof keyId !== 'string' || typeof value !== 'string' || counted.has(keyId)) {
      continue;
    }
    const publicKeyB64 = allowedKeys.get(keyId);
    if (publicKeyB64 === undefined) {
      continue;
    }
    if (await verifyWithKey(publicKeyB64, material, value)) {
      counted.add(keyId);
      matching++;
    }
  }
  return matching;
}

/** Whether [signatures] clear the [threshold] over the canonical form of [body]. */
export async function thresholdVerifyBody(
  body: unknown,
  signatures: unknown,
  allowedKeys: ReadonlyMap<string, string>,
  threshold: number,
): Promise<boolean> {
  const material = utf8(canonical(body));
  return (await countValidSignatures(material, signatures, allowedKeys)) >= threshold;
}
